import { Fragment } from 'react';
import { array, nonEmptyArray, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { Day } from '@mews/enums';
import { intersperse } from '@mews/fp';
import { upperFirst } from '@mews/std';
import { DaysParameters, Restriction } from '@mews-commander/data-access';
import { SpacedComma, SpacedDash } from '@mews-commander/ui';
import { LocalizedEnumify } from '@mews/framework';

// Not ideal, since not all cultures start their weekday with 'monday' but the list view ignores this as well...
const orderedDayNames: Array<keyof DaysParameters> = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

const DayName = ({ day }: { day: keyof DaysParameters }) => (
    <LocalizedEnumify enumValue={Day.getByName(upperFirst(day))} />
);

interface DayRangesProps {
    days: NonNullable<Restriction['conditions']['daysNew']>;
}

export const DayRanges = ({ days }: DayRangesProps) =>
    pipe(
        orderedDayNames,
        array.mapWithIndex((index, day) => ({ dayIndex: index, day, enabled: days[day] })),
        array.filter(({ enabled }) => enabled),
        array.mapWithIndex((index, value) => ({ index, ...value })),
        option.fromPredicate(array.isNonEmpty),
        option.map(
            flow(
                nonEmptyArray.groupBy(({ index, dayIndex }) => (index - dayIndex).toString()),
                Object.values<Array<{ day: keyof DaysParameters }>>,
                array.map((range) => (range.length > 1 ? [range[0], range[range.length - 1]] : [range[0]])),
                array.map(array.map(({ day }) => <DayName key={day} day={day} />)),
                array.mapWithIndex((index, range) =>
                    pipe(
                        range,
                        intersperse((dashIndex) => <Fragment key={`dash-${index}-${dashIndex}`}>{SpacedDash}</Fragment>)
                    )
                ),
                array.mapWithIndex((index, range) => <span key={`range-${index}`}>{range}</span>),
                intersperse((index) => <Fragment key={`comma-${index}`}>{SpacedComma}</Fragment>)
            )
        ),
        option.match(
            () => null,
            (dayRanges) => <>{dayRanges}</>
        )
    );
